import { useQuery } from '@tanstack/react-query';
import { useDebounce } from 'src/hooks/useUtils';
import { tenantSearch } from 'src/services/apis/tenantRequests';
import { useTenantContext } from 'src/context/useTenantContext';
import { QUERIES } from 'src/utils/react-query';

export interface RecordHit {
  type: string;
  id: string;
  title: string;
  subtitle?: string;
  targetRoute: string;
  targetParams: Record<string, string>;
}

/** Two characters is the server's DTO floor; anything shorter is not worth a round trip. */
export function shouldQueryRecords(query: string): boolean {
  return query.trim().length >= 2;
}

export function toRecordHits(response: { hits?: RecordHit[] } | undefined): RecordHit[] {
  if (!response?.hits) return [];
  // A hit with no title would render as a blank, unclickable row.
  return response.hits.filter((hit) => Boolean(hit?.title) && Boolean(hit?.targetRoute));
}

export function useSearchRecords(query: string) {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  const debounced = useDebounce(query, 250);
  const enabled = shouldQueryRecords(debounced);

  const result = useQuery(
    [QUERIES.GLOBAL_SEARCH, orgSlug, restaurantId, branchId, debounced],
    () => tenantSearch({ orgSlug, restaurantId, branchId }, { q: debounced.trim(), limit: 3 }),
    {
      enabled,
      cacheTime: 0,
      // Sections would otherwise flash empty between keystrokes.
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  return {
    hits: toRecordHits(result.data as never),
    isFetching: enabled && result.isFetching,
    isError: result.isError,
    retry: () => result.refetch(),
  };
}
